/**
 * Transition picker sitting on the boundary between two scenes.
 */

import { useEffect, useRef, useState } from 'react';
import { VIDEO_MAX_TRANSITION_MS, type VideoScene } from '@canvas/shared-types';
import { TRANSITIONS, isHardCut, transitionDef } from '@/data/transitions';
import { useVideoStore } from '@/stores/videoStore';

interface TransitionChipProps {
  /** The incoming scene; transitions belong to the scene they lead INTO. */
  scene: VideoScene;
  x: number;
}

export default function TransitionChip({ scene, x }: TransitionChipProps) {
  const setTransition = useVideoStore((s) => s.setTransition);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const current = scene.transitionIn;
  const def = transitionDef(current.id);
  const cut = isHardCut(current.id);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="vt-transition" ref={rootRef} style={{ left: x }}>
      <button
        type="button"
        className={`vt-transition-chip ${cut ? 'cut' : ''}`}
        title={`Transition: ${def.label}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {cut ? '|' : '⋈'}
      </button>
      {open && (
        <div className="vt-transition-pop" role="dialog" aria-label="Transition">
          <div className="vt-transition-list">
            {TRANSITIONS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={t.id === current.id ? 'active' : ''}
                title={t.hint}
                onClick={() =>
                  setTransition(scene.pageId, {
                    ...current,
                    id: t.id,
                    durationMs: t.defaultDurationMs,
                    color: t.supportsColor ? current.color || '#000000' : undefined,
                  })
                }
              >
                {t.label}
              </button>
            ))}
          </div>
          {!cut && (
            <label className="vt-transition-duration">
              <span>{(current.durationMs / 1000).toFixed(2)}s</span>
              <input
                type="range"
                min={100}
                max={VIDEO_MAX_TRANSITION_MS}
                step={50}
                value={current.durationMs}
                onPointerDown={() => useVideoStore.getState().checkpoint()}
                onChange={(e) =>
                  setTransition(
                    scene.pageId,
                    { ...current, durationMs: Number(e.target.value) },
                    { record: false }
                  )
                }
              />
            </label>
          )}
          {def.supportsColor && (
            <label className="vt-transition-color">
              <span>Color</span>
              <input
                type="color"
                value={current.color || '#000000'}
                onChange={(e) => setTransition(scene.pageId, { ...current, color: e.target.value })}
              />
            </label>
          )}
        </div>
      )}
    </div>
  );
}
